function escapeText(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function innerText(node) {
  const text = (node.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text;
}

export function wordParagraph(text, bold = false) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return "";
  const rPr = bold ? "<w:rPr><w:b/></w:rPr>" : "";
  return (
    "<w:p><w:r>" +
    rPr +
    '<w:t xml:space="preserve">' +
    escapeText(trimmed) +
    "</w:t></w:r></w:p>"
  );
}

function tableToXml(tableEl) {
  const rows = [];
  tableEl.querySelectorAll("tr").forEach((tr) => {
    const cells = [];
    tr.querySelectorAll("td, th").forEach((cell) => {
      cells.push(innerText(cell));
    });
    if (cells.length) rows.push(cells);
  });

  if (!rows.length) return "";

  const colCount = Math.max(...rows.map((r) => r.length));
  const colWidth = Math.floor(9000 / colCount);

  let tbl =
    "<w:tbl><w:tblPr>" +
    '<w:tblW w:w="5000" w:type="pct"/>' +
    "<w:tblBorders>" +
    '<w:top w:val="single" w:sz="4" w:space="0" w:color="auto"/>' +
    '<w:left w:val="single" w:sz="4" w:space="0" w:color="auto"/>' +
    '<w:bottom w:val="single" w:sz="4" w:space="0" w:color="auto"/>' +
    '<w:right w:val="single" w:sz="4" w:space="0" w:color="auto"/>' +
    '<w:insideH w:val="single" w:sz="4" w:space="0" w:color="auto"/>' +
    '<w:insideV w:val="single" w:sz="4" w:space="0" w:color="auto"/>' +
    "</w:tblBorders></w:tblPr>";

  rows.forEach((cells, rowIndex) => {
    const isHeader =
      rowIndex === 0 &&
      rows.length > 1 &&
      cells.join("|") !== rows[1].join("|");
    tbl += "<w:tr>";
    for (let c = 0; c < colCount; c++) {
      const text = cells[c] || "";
      tbl +=
        '<w:tc><w:tcPr><w:tcW w:w="' +
        colWidth +
        '" w:type="dxa"/></w:tcPr><w:p><w:r>';
      if (isHeader) tbl += "<w:rPr><w:b/></w:rPr>";
      tbl +=
        '<w:t xml:space="preserve">' +
        escapeText(text) +
        "</w:t></w:r></w:p></w:tc>";
    }
    tbl += "</w:tr>";
  });

  tbl += "</w:tbl>";
  return tbl;
}

function listToXml(listEl, ordered) {
  let out = "";
  let index = 1;
  listEl.querySelectorAll(":scope > li").forEach((li) => {
    const prefix = ordered ? index + ". " : "• ";
    out += wordParagraph(prefix + innerText(li));
    index++;
  });
  return out;
}

function nodeToXml(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = innerText(node);
    return text ? wordParagraph(text) : "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const tag = node.tagName.toLowerCase();
  switch (tag) {
    case "p":
      return wordParagraph(innerText(node));
    case "br":
      return "<w:p><w:r><w:br/></w:r></w:p>";
    case "table":
      return tableToXml(node);
    case "figure": {
      const table = node.querySelector("table");
      return table ? tableToXml(table) : childrenToXml(node);
    }
    case "ul":
      return listToXml(node, false);
    case "ol":
      return listToXml(node, true);
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
      return wordParagraph(innerText(node), true);
    case "div":
    case "section":
    case "article":
    case "blockquote":
      return childrenToXml(node);
    default:
      return childrenToXml(node);
  }
}

function childrenToXml(parent) {
  let out = "";
  parent.childNodes.forEach((child) => {
    out += nodeToXml(child);
  });
  return out;
}

/** Converte HTML do CKEditor em fragmentos WordprocessingML. */
export function htmlToWordXml(html) {
  const trimmed = String(html || "").trim();
  if (!trimmed) return "";

  const doc = new DOMParser().parseFromString(
    '<div id="ck-root">' + trimmed + "</div>",
    "text/html"
  );
  const root = doc.getElementById("ck-root");
  if (!root) return wordParagraph(trimmed.replace(/<[^>]+>/g, ""));

  return childrenToXml(root);
}

const PLACEHOLDER_VARIANTS = (key) => [
  "{{ " + key + " }}",
  "{{" + key + "}}",
  "{{ " + key + "}}",
  "{{" + key + " }}",
];

/** Substitui o parágrafo que contém {{ $kit }} pelo bloco XML (tabelas). */
export function replaceBlockPlaceholderInXml(xml, key, wordXml) {
  if (!wordXml) return xml;

  let result = xml;
  for (const placeholder of PLACEHOLDER_VARIANTS(key)) {
    const pattern =
      "<w:p\\b[^>]*>(?:(?!<\\/w:p>)[\\s\\S])*?" +
      placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") +
      "(?:(?!<\\/w:p>)[\\s\\S])*?<\\/w:p>";
    const re = new RegExp(pattern);
    if (re.test(result)) {
      result = result.replace(re, wordXml);
      break;
    }
  }
  return result;
}

export function applyBlockReplacementsToZip(zip, blockFields) {
  if (!blockFields || typeof blockFields !== "object") return;

  const partRe =
    /^word\/(document|header\d+|footer\d+|footnotes|endnotes)\.xml$/;

  Object.keys(zip.files).forEach((name) => {
    if (!partRe.test(name)) return;
    const file = zip.files[name];
    if (!file || file.dir) return;
    let xml = file.asText();
    Object.entries(blockFields).forEach(([key, wordXml]) => {
      if (wordXml) {
        xml = replaceBlockPlaceholderInXml(xml, key, wordXml);
      }
    });
    zip.file(name, xml);
  });
}
